/**
 * AddProviderDialog — the Care providers card's "+ Add" (record-add
 * slice). Founder framing (2026-07-06 live test): adding a provider is
 * adding them TO THE CLIENT'S CARE TEAM, linked to the Directory when
 * possible — so the flow leads with the Directory picker (the house
 * inline search, ReferralSourceField pattern: api.searchProviders
 * returns orgs AND individuals) and the unlinked-by-name path is the
 * VISIBLY MARKED fallback (steward review can link it later).
 *
 * POSTs the providers add route (the [B1] three-arm union: one
 * directory arm OR zero arms + display_name). Fields derive from the
 * ONE registry (PROVIDER_SPEC direct-form fields: display_name required;
 * role / office_phone / office_address optional — specialty and
 * organization are directoryOnly, rendering from the linked Directory
 * entry on every read, so the form never offers them and the API
 * refuses them, WS-5). A pick prefills the name and fills the office
 * phone, the address and the email with the entry's, tagged "From the
 * Directory"; specialty and practice show read-only beside them. A box
 * changed from the entry's is "Just for this client", with "Use the
 * Directory's" to put the entry's back. Only a box that differs from
 * the entry travels, so an untouched one keeps following the Directory.
 * Success invalidates the ['clientRecord'] aggregate: one audited
 * re-read, never a cache patch.
 */

import { useSupportContact } from '@/hooks/useSupportContact';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SearchResult } from '@/lib/types';
import { PROPOSAL_PAYLOAD_SPECS, directFormFields } from '@/lib/noteVocabulary';
import { postRecordRow, recordEditErrorMessage } from '@/lib/clientRecordEditApi';
import { cachedClientRecord } from '@/lib/clientRecordApi';
import { pronounForms } from '@/lib/pronounForms';
import { recordDialogTitle, recordPrimaryLabel } from '@/lib/recordFormWords';
import { DIRECTORY_SEARCH_MIN_CHARS, directoryResultLine, useDirectorySearch } from '@/lib/directorySearch';
import { textBoxProps } from '@/lib/textBoxProps';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { SearchField } from '@/components/primitives/SearchField';
import { Field } from '@/components/primitives/Field';
import { TextArea, TextInput } from '@/components/primitives/TextInput';
import { FLD, FLD_LABEL, FLD_TEXTAREA } from '@/components/primitives/modalFormStyles';
import { fieldLabel as humanize } from '@/lib/fieldLabels';
import { PHONE_EXAMPLE, formatPhoneDisplay, formatPhoneInput, normalizePhone, phoneProblem } from '@/lib/phone';
import { contactEmailProblem } from '@/lib/clientBillingContacts';
import { api } from '@/lib/api';
import { DirectoryAddDoor } from '@/components/clientRecord/DirectoryAddDoor';
import {
  AddressCheckChoices,
  AddressCheckPrompt,
  AddressFields,
  useAddressForm,
  type AddressFormParts,
} from '@/components/clientRecord/AddressesCard';

type DirectoryLink =
  | { kind: 'individual'; id: string; name: string; row: SearchResult }
  | { kind: 'org'; id: string; name: string; row: SearchResult }
  | null;

/** The boxes a pick fills, and the entry's value each shows. */
const FILLED_BOXES = ['office_phone', 'email'] as const;

/** The entry's value for a box it fills, as the box shows it ('' when
 *  none). */
function entryValue(link: DirectoryLink, field: string): string {
  if (link === null) return '';
  if (field === 'office_phone') return link.row.phone ? formatPhoneDisplay(link.row.phone) : '';
  if (field === 'email') return link.row.email ?? '';
  return '';
}

/** Whether a box holds the entry's value (a phone by its digits, an
 *  email in any case). */
function isEntryValue(link: DirectoryLink, field: string, cur: string): boolean {
  const dir = entryValue(link, field).trim();
  if (dir === '' || cur.trim() === '') return false;
  if (field === 'office_phone') return normalizePhone(cur) === normalizePhone(dir);
  if (field === 'email') return cur.trim().toLowerCase() === dir.toLowerCase();
  return cur.trim() === dir;
}

/** Two addresses' parts are the same, trimmed and case folded. */
function sameParts(a: AddressFormParts, b: AddressFormParts): boolean {
  const n = (v: string | null): string => (v ?? '').trim().toLowerCase();
  return (['line1', 'line2', 'city', 'state', 'zip'] as const).every((k) => n(a[k]) === n(b[k]));
}

/** The address fields' values as parts, blanks as null. */
function partsOfValues(v: { line1: string; line2: string; city: string; state: string; zip: string }): AddressFormParts {
  const n = (x: string): string | null => (x.trim() === '' ? null : x.trim());
  return { line1: n(v.line1), line2: n(v.line2), city: n(v.city), state: n(v.state), zip: n(v.zip) };
}

export interface AddProviderDialogProps {
  onClose: () => void;
}

export function AddProviderDialog({ onClose }: AddProviderDialogProps): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const queryClient = useQueryClient();
  const forms = pronounForms(cachedClientRecord(queryClient, tenantSlug, clientId)?.vitals.pronouns);

  const fields = directFormFields(PROPOSAL_PAYLOAD_SPECS.provider_new);
  const [values, setValues] = useState<Record<string, string>>({});
  const [link, setLink] = useState<DirectoryLink>(null);
  const [error, setError] = useState<string | null>(null);
  // The error copy's contact, from the shell's cached tenant config.
  const contact = useSupportContact({ enabled: false });
  // Office-phone on-blur message (Design Pass 3; save-block derives live).
  const [phoneMessage, setPhoneMessage] = useState<string | null>(null);
  // The email's advisory sentence, on leaving the box; never a block.
  const [emailMessage, setEmailMessage] = useState<string | null>(null);
  // A pick unmounts the search, so focus moves to the control that took
  // its place: Change.
  const changeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (link !== null) changeRef.current?.focus();
  }, [link]);

  const set = (field: string, val: string): void => {
    setError(null);
    setValues((prev) => ({ ...prev, [field]: val }));
  };

  // 0234: the office address is the house address form. A pick fills it
  // with the entry's parts (read from the entry), tagged; only an address
  // that differs from the entry's travels.
  const [entryParts, setEntryParts] = useState<AddressFormParts | null>(null);
  const addressForm = useAddressForm({
    existing: null,
    rule: 'street',
    idPrefix: 'provider-office-address',
    done: false,
    saving: false,
    onSave: (parts) => saveMut.mutate({ values, link, address: parts }),
  });
  const addressFromEntry = (): boolean =>
    entryParts !== null && sameParts(partsOfValues(addressForm.fields.values), entryParts);

  /** The entry's parts into the address form, when it shows nothing of
   *  this client's own (empty, or the previous entry's). */
  async function fillAddressFrom(next: DirectoryLink): Promise<void> {
    const showingEntry = addressForm.isEmpty() || addressFromEntry();
    if (next === null) {
      if (showingEntry) addressForm.reset(null);
      setEntryParts(null);
      return;
    }
    let parts: AddressFormParts | null = null;
    try {
      const d = next.kind === 'individual' ? await api.getIndividual(tenantSlug, next.id) : await api.getProvider(tenantSlug, next.id);
      const line1 = (d.address_line1 ?? '').trim();
      parts =
        line1 === ''
          ? null
          : { line1, line2: d.address_line2 ?? null, city: d.address_city ?? null, state: d.address_state ?? null, zip: d.address_zip ?? null };
    } catch {
      // The link still saves; the fields keep what they hold.
    }
    setEntryParts(parts);
    if (showingEntry) addressForm.reset(parts);
  }

  /** A pick, or Change back to the search (null): a filled box still
   *  showing the previous entry's value takes the new entry's, or
   *  empties. */
  function relink(next: DirectoryLink): void {
    void fillAddressFrom(next);
    setError(null);
    setPhoneMessage(null);
    setValues((prev) => {
      const out = { ...prev };
      for (const field of FILLED_BOXES) {
        const cur = out[field] ?? '';
        if (cur.trim() === '' || isEntryValue(link, field, cur)) out[field] = entryValue(next, field);
      }
      if (next !== null && (out.display_name ?? '').trim() === '') out.display_name = next.name;
      return out;
    });
    setLink(next);
  }

  // The fields and the link are read at the press and travel as the
  // variables; the error names the fields that were sent.
  const saveMut = useMutation<unknown, unknown, { values: typeof values; link: DirectoryLink; address?: AddressFormParts }>({
    mutationFn: (v) => {
      const out: Record<string, unknown> = {};
      for (const [field, fs] of fields) {
        if (fs.kind === 'postal_address') continue;
        const typed = (v.values[field] ?? '').trim();
        // A box still showing the entry's value is not this client's
        // own: it stays out, and the entry's shows through on read.
        if (typed === '' || isEntryValue(v.link, field, typed)) continue;
        // Phone travels normalized (digits / "+digits"); display re-formats.
        // An email travels lower-cased, as it is stored.
        out[field] =
          field === 'office_phone' ? normalizePhone(typed) : fs.kind === 'email' ? typed.toLowerCase() : typed;
      }
      if (v.address !== undefined) out.office_address = v.address;
      if (v.link?.kind === 'individual') out.directory_individual_id = v.link.id;
      if (v.link?.kind === 'org') out.directory_org_id = v.link.id;
      return postRecordRow(tenantSlug, clientId, 'providers', out);
    },
    onSuccess: async () => {
      // ONE audited re-read of the aggregate.
      await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
      onClose();
    },
    onError: (err, v) =>
      setError(recordEditErrorMessage(err, { specs: PROPOSAL_PAYLOAD_SPECS.provider_new, sent: v.values }, contact)),
  });

  const displayName = (values.display_name ?? '').trim();
  const canSave =
    displayName !== '' &&
    phoneProblem(values.office_phone ?? '') === null &&
    !saveMut.isPending &&
    !addressForm.checking;

  /** Add: an address of this client's own goes through the address form
   *  (its gaps and its check); none, or the entry's, sends none. */
  function onAdd(): void {
    if (addressForm.isEmpty() || addressFromEntry()) {
      saveMut.mutate({ values, link });
      return;
    }
    addressForm.pressSave();
  }

  /** The line under the office address: where it comes from, and the way
   *  back to the entry's from an address of this client's own. */
  function addressSourceLine(): ReactNode {
    if (link === null || entryParts === null) return null;
    const fromDirectory = addressFromEntry();
    if (!fromDirectory && addressForm.isEmpty()) return null;
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ink-soft">
        <span data-testid="provider-field-office_address-source">{fromDirectory ? 'From the Directory' : 'Just for this client'}</span>
        {!fromDirectory && (
          <Button
            variant="ghost"
            size="sm"
            data-testid="provider-field-office_address-use-directory"
            onClick={() => addressForm.reset(entryParts)}
          >
            {"Use the Directory's"}
          </Button>
        )}
      </div>
    );
  }

  /** The line under a filled box: where its value comes from, and the
   *  way back to the entry's from a value of this client's own. */
  function sourceLine(field: string): ReactNode {
    const dir = entryValue(link, field);
    if (link === null || dir === '') return null;
    const cur = values[field] ?? '';
    const fromDirectory = isEntryValue(link, field, cur);
    if (!fromDirectory && cur.trim() === '') return null;
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ink-soft">
        <span data-testid={`provider-field-${field}-source`}>
          {fromDirectory ? 'From the Directory' : 'Just for this client'}
        </span>
        {!fromDirectory && (
          <Button
            variant="ghost"
            size="sm"
            data-testid={`provider-field-${field}-use-directory`}
            onClick={() => {
              setPhoneMessage(null);
              setEmailMessage(null);
              set(field, dir);
            }}
          >
            {"Use the Directory's"}
          </Button>
        )}
      </div>
    );
  }

  const labelOf = (field: string): string => (field === 'role' ? `Role in ${forms.possessive} care` : humanize(field));
  const helpOf = (field: string): string | undefined =>
    field === 'role' ? 'For example Cardiologist, Primary care doctor.' : undefined;

  // What the entry says that the row never stores: shown, not typed.
  const readOnly: Array<[string, string]> = [];
  if (link?.row.specialty) readOnly.push(['Specialty', link.row.specialty]);
  if (link?.row.practice) readOnly.push(['Practice or organization', link.row.practice]);

  return (
    <Dialog
      title={recordDialogTitle('providers', 'create')}
      size="xl"
      testId="add-provider-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          {addressForm.prompting ? (
            <AddressCheckChoices
              phase={addressForm.check.phase}
              busy={saveMut.isPending}
              onKeep={() => addressForm.choose('checked', false)}
              onUse={() => addressForm.choose('checked', true)}
              onSaveAsTyped={() => addressForm.choose('saved_as_typed', false)}
            />
          ) : (
            <Button
              variant="primary"
              size="sm"
              disabled={!canSave}
              loading={saveMut.isPending || addressForm.checking}
              data-testid="add-provider-save"
              onClick={onAdd}
            >
              {recordPrimaryLabel('providers', 'create')}
            </Button>
          )}
        </>
      }
    >
      <p className="text-xs text-ink-soft mb-3">
        Find them in the Directory so {forms.possessive} record carries their
        details and keeps up with them. Not there yet? Type their name below;
        you can link them later.{' '}
        {/* The directory map, already centred on this client (?near=): the
            same filtered directory, seen by distance from their home. */}
        <Link
          to={`/t/${encodeURIComponent(tenantSlug)}/directory?map=1&near=${encodeURIComponent(clientId)}`}
          className="font-semibold text-sage-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          data-testid="add-provider-directory-map"
        >
          Find one near {forms.object} on the map
        </Link>
      </p>

      {/* Directory link FIRST (founder framing: link when possible). */}
      {link !== null ? (
        <div
          data-testid="add-provider-linked"
          className="mb-3 flex items-center gap-2 rounded-base border border-sage-200 bg-sage-50/60 px-2.5 py-1.5 text-sm"
        >
          <span className="text-sage-800">
            Linked to the Directory: <span className="font-semibold">{link.name}</span>
          </span>
          <Button
            ref={changeRef}
            variant="ghost"
            size="sm"
            className="ml-auto"
            data-testid="add-provider-change"
            onClick={() => relink(null)}
          >
            Change
          </Button>
        </div>
      ) : (
        <DirectorySearch
          onPick={(row) =>
            relink(
              row.type === 'organization'
                ? { kind: 'org', id: row.id, name: row.display_name, row }
                : { kind: 'individual', id: row.id, name: row.display_name, row },
            )
          }
        />
      )}

      {/* Design Pass 3 (2): the one dialog scale — full FLD fields on a
          comfortable two-column grid; prose-likely (multiline) fields
          span the row; the phone field formats/validates like the
          contact dialog's. */}
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 py-2 sm:grid-cols-2">
        {fields.map(([field, fs]) =>
          fs.kind === 'postal_address' ? (
            <fieldset key={field} className="flex min-w-0 flex-col gap-2 sm:col-span-2">
              <legend className={FLD_LABEL}>{labelOf(field)}</legend>
              <AddressFields idPrefix="provider-office-address" {...addressForm.fields} line1Required={false} />
              {addressSourceLine()}
              <AddressCheckPrompt phase={addressForm.check.phase} />
            </fieldset>
          ) : (
          <div key={field} className={fs.multiline === true ? 'sm:col-span-2' : undefined}>
            <Field label={labelOf(field)} required={fs.required} help={helpOf(field)} htmlFor={`add-provider-${field}`}>
              {/* The phone formats as you type; the name and the role take
                  their name case on leaving the box. */}
              {fs.multiline === true ? (
                <TextArea
                  id={`add-provider-${field}`}
                  data-testid={`add-field-${field}`}
                  rows={3}
                  className={FLD_TEXTAREA}
                  value={values[field] ?? ''}
                  onChange={(e) => set(field, e.target.value)}
                />
              ) : field === 'office_phone' ? (
                <>
                  <TextInput
                    id={`add-provider-${field}`}
                    data-testid={`add-field-${field}`}
                    type="tel"
                    placeholder={PHONE_EXAMPLE}
                    className={FLD}
                    value={values[field] ?? ''}
                    onChange={(e) => {
                      setPhoneMessage(null);
                      set(field, formatPhoneInput(e.target.value));
                    }}
                    onBlur={() => setPhoneMessage(phoneProblem(values[field] ?? ''))}
                  />
                  {phoneMessage !== null && (
                    <p
                      data-testid="add-field-office_phone-problem"
                      className="mt-1.5 text-[12.5px] text-pepper"
                    >
                      {phoneMessage}
                    </p>
                  )}
                </>
              ) : fs.kind === 'email' ? (
                <>
                  <TextInput
                    id={`add-provider-${field}`}
                    data-testid={`add-field-${field}`}
                    type="email"
                    spellCheck={false}
                    autoCapitalize="none"
                    autoComplete="off"
                    className={FLD}
                    value={values[field] ?? ''}
                    onChange={(e) => {
                      setEmailMessage(null);
                      set(field, e.target.value);
                    }}
                    onBlur={() => {
                      const typed = values[field] ?? '';
                      setEmailMessage(isEntryValue(link, field, typed) ? null : contactEmailProblem(typed));
                    }}
                  />
                  {emailMessage !== null && (
                    <p data-testid="add-field-email-problem" className="mt-1.5 text-xs text-pepper">
                      {emailMessage}
                    </p>
                  )}
                </>
              ) : (
                <TextInput
                  id={`add-provider-${field}`}
                  data-testid={`add-field-${field}`}
                  type="text"
                  className={FLD}
                  value={values[field] ?? ''}
                  onChange={(e) => set(field, e.target.value)}
                  {...textBoxProps(fs.textKind)}
                />
              )}
              {sourceLine(field)}
            </Field>
          </div>
          ),
        )}
        {readOnly.map(([label, value]) => (
          <div key={label} data-testid="add-provider-directory-fact" className="flex min-w-0 flex-col gap-1">
            <span className={FLD_LABEL}>{label}</span>
            <span className="text-sm text-ink">{value}</span>
            <span className="text-xs text-ink-soft">From the Directory.</span>
          </div>
        ))}
      </div>

      {/* The unlinked path stays honest and visible (stewardship later). */}
      {link === null && displayName !== '' && (
        <p data-testid="add-provider-unlinked-note" className="text-xs text-ink-soft mb-2">
          They’ll be on this client’s care team by name. To share their details with other clients, add them to the Directory too.
        </p>
      )}

      {error !== null && (
        <p role="alert" data-testid="add-provider-error" className="text-xs text-pepper">
          {error}
        </p>
      )}
    </Dialog>
  );
}

export interface DirectorySearchProps {
  onPick: (row: SearchResult) => void;
  /** Escape on an empty box. Unset, the key bubbles (in a dialog, to the
   *  dialog's own close); the confirm queue, which has no dialog, passes
   *  it to close the search. */
  onEscapeEmpty?: () => void;
}

/** The house inline Directory search (ReferralSourceField pattern):
 *  orgs AND individuals; debounce + min-chars; picking links the row.
 *  Exported for the sibling linking surfaces (LinkProviderDialog + the
 *  ConfirmQueue's provider_new card).
 *
 *  The box is a combobox over a listbox of results, so a row can be
 *  picked from the keyboard: ArrowDown and ArrowUp move the active
 *  result and stop at the ends; Enter picks the active result the way a
 *  click does, and does nothing when none is active. The "Searching…"
 *  and "No Directory matches" rows are never results. Every answered list
 *  ends with the Directory's add or request door (DirectoryAddDoor), match
 *  or not (LP-26); a listing added there is picked as a result is. Escape with text
 *  is SearchField's own: it empties the box. Every host unmounts the
 *  search on a pick and focuses the control that takes its place. */
export function DirectorySearch({ onPick, onEscapeEmpty }: DirectorySearchProps): ReactNode {
  const [query, setQuery] = useState('');
  // The active result's index; -1 for none. A query change clears it.
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = useId();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const q = query.trim();
  const open = q.length >= DIRECTORY_SEARCH_MIN_CHARS;
  const { rows: results, loading } = useDirectorySearch({ q, enabled: true, limit: 20 });

  const optionId = (row: SearchResult): string => `${listboxId}-${row.type}-${row.id}`;
  const activeRow = active >= 0 && active < results.length ? results[active] : undefined;
  const activeId = activeRow === undefined ? undefined : optionId(activeRow);

  // Keep the active result in view (where the method exists).
  useEffect(() => {
    if (activeId === undefined) return;
    const el = document.getElementById(activeId);
    if (el !== null && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (results.length > 0) setActive((i) => Math.min(i + 1, results.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? i : i - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeRow !== undefined) onPick(activeRow);
      return;
    }
    if (e.key === 'Escape' && query === '' && onEscapeEmpty !== undefined) {
      e.preventDefault();
      e.stopPropagation();
      onEscapeEmpty();
    }
  }

  return (
    <div className="mb-2">
      <SearchField
        ref={inputRef}
        value={query}
        onChange={(next) => {
          setQuery(next);
          setActive(-1);
        }}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-activedescendant={activeId}
        aria-label="Search the Directory"
        testId="add-provider-search"
        placeholder="Search the Directory"
      />
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Directory results"
          className="mt-1 max-h-44 overflow-y-auto rounded-base border border-sage-200 bg-paper"
        >
          {loading && <li className="px-2 py-1 text-[12px] text-sage-400">Searching…</li>}
          {!loading && results.length === 0 && (
            <li className="px-2 py-1 text-[12px] text-sage-400">
              No match in the Directory yet. Use the button below to add them, or add them to this client by name.
            </li>
          )}
          {results.map((row, i) => (
            <li
              key={`${row.type}-${row.id}`}
              id={optionId(row)}
              role="option"
              aria-selected={i === active}
              data-testid="add-provider-option"
              onMouseDown={(e) => {
                e.preventDefault();
                onPick(row);
              }}
              className={`flex cursor-pointer items-baseline gap-2 px-2 py-1 text-[13px] text-ink hover:bg-sage-50${
                i === active ? ' bg-sage-50' : ''
              }`}
            >
              <span className="w-12 flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-sage-400">
                {row.type === 'organization' ? 'Org' : 'Person'}
              </span>
              <span className="flex min-w-0 flex-col">
                <span>{row.display_name}</span>
                {directoryResultLine(row) !== '' && (
                  <span className="text-xs text-ink-soft">{directoryResultLine(row)}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <DirectoryAddDoor
        query={query}
        settled={open && !loading}
        onAdded={onPick}
        testId="add-provider-directory-door"
      />
    </div>
  );
}
